/**
 * Période affichée — bloc de mise en page qui rappelle la période effective du rapport.
 * Aucune requête de données.
 *
 * La période du rapport vit au niveau de la page (`PeriodPicker` → `useWidgetData`).
 * Le widget l'accepte donc en prop optionnelle `period` : quand `WidgetRenderer` la
 * transmettra (câblage de la page), elle prime. À défaut, on retombe sur la période
 * propre du widget (`config.period` en mode `custom`), puis sur les dates renvoyées
 * par le backend dans `meta`.
 */
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import type { WidgetConfig } from '../schemas';

type Row = Record<string, string | number>;

export interface EffectivePeriod {
  start: string;
  end: string;
}

interface WidgetData {
  current: Row[];
  previous: Row[] | null;
  meta: any;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetData | null;
  loading?: boolean;
  error?: string;
  /** Période effective du rapport (transmise par la page si disponible). */
  period?: EffectivePeriod | null;
}

// Cadre fourni par `WidgetShell` (WidgetRenderer) : ici, padding + hauteur + centrage.
const SHELL = 'h-full flex items-center justify-center';

/** GA4 renvoie parfois `20260601` : on ramène tout à l'ISO avant formatage. */
function toIsoDate(value: string): string {
  if (/^\d{8}$/.test(value)) return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  return value;
}

/** « Du 1 juin 2026 au 30 juin 2026 ». */
export function formatPeriodLabel(start: string, end: string): string {
  const from = dayjs(toIsoDate(start)).locale('fr').format('D MMMM YYYY');
  const to = dayjs(toIsoDate(end)).locale('fr').format('D MMMM YYYY');
  return `Du ${from} au ${to}`;
}

/** Période à afficher : prop du rapport > période custom du widget > dates du backend. */
export function resolvePeriod(
  config: WidgetConfig,
  data: WidgetData | null,
  period: EffectivePeriod | null | undefined,
): EffectivePeriod | null {
  if (period?.start && period.end) return { start: period.start, end: period.end };

  const own = config.period;
  if (own?.mode === 'custom' && own.start && own.end) return { start: own.start, end: own.end };

  const meta = data?.meta as { start?: string; end?: string } | undefined;
  if (meta?.start && meta.end) return { start: meta.start, end: meta.end };

  return null;
}

export default function DateRangeDisplayWidget({ config, data, loading, error, period }: WidgetProps) {
  if (loading) {
    return (
      <div className={`${SHELL} p-2`}>
        <div className="h-4 w-2/3 animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={SHELL}>
        <span className="text-sm text-red-600">{error}</span>
      </div>
    );
  }

  const effective = resolvePeriod(config, data, period);

  if (!effective) {
    return (
      <div className={SHELL}>
        <span className="text-sm text-[#8B8FA7] italic">Aucune période</span>
      </div>
    );
  }

  return (
    <div className={SHELL}>
      <span className="text-sm font-medium text-[#1B2559]">
        {formatPeriodLabel(effective.start, effective.end)}
      </span>
    </div>
  );
}
